import { selectLocaleObject } from '~/components/brand/ducks/selectors';
import { useSelector } from 'react-redux';
import { useTypedTranslations } from '../components/translation/use-translations';
import { useCallback } from 'react';

enum ReadableDateStringTextKeys {
	TODAY = 'TODAY',
	TOMORROW = 'TOMORROW'
}

// Helper functions
function getFlooredStartDate(date: Date | string | number) {
	const time = new Date(date);
	// We don't care about the time of day when we want to group by date
	time.setHours(0, 0, 0, 0);
	return time;
}

export function isDateToday(startDate: string) {
	const today = getFlooredStartDate(new Date(Date.now()));
	const flooredStartDate = getFlooredStartDate(startDate);

	return today.getTime() === flooredStartDate.getTime();
}

export function isDateTomorrow(startDate: string) {
	const now = new Date(Date.now());
	const today = getFlooredStartDate(now);
	const tomorrow = getFlooredStartDate(now.setDate(today.getDate() + 1));
	const flooredStartDate = getFlooredStartDate(startDate);

	return flooredStartDate.getTime() === tomorrow.getTime();
}

export function isDateLessThanXDaysAway(startDate: string, daysToAdd: number) {
	const now = new Date(Date.now());
	const today = getFlooredStartDate(now);
	const daysFromNow = getFlooredStartDate(now.setDate(today.getDate() + daysToAdd));
	const flooredStartDate = getFlooredStartDate(startDate);

	return flooredStartDate.getTime() <= daysFromNow.getTime();
}

function getTranslatedDate(date: string, isoCode: string, options?: Intl.DateTimeFormatOptions) {
	return new Intl.DateTimeFormat(isoCode, options).format(new Date(date));
}

export function capitalizeFirstLetter(word: string): string {
	return word.charAt(0).toUpperCase() + word.slice(1);
}

/**
 *
 * @description Returns function that formats a date string into a relative, translated string that is easier for the user to read and parse
 *
 * @returns Depending on the time from now to the date you passed the returned string will vary but as an example:
 * - Today, 03 October
 * - Tomorrow, 04 October
 * - Wednesday 05 October
 * - Thursday 06 October
 * - (After 5 days, stop using weekday)
 * - 08 October
 * - 10 October
 */
function useGetRelativeDate(options?: {
	formatMonthOptions?: Intl.DateTimeFormatOptions;
	formatWeekdayOptions?: Intl.DateTimeFormatOptions;
	formatDayOptions?: Intl.DateTimeFormatOptions;
}) {
	const { code } = useSelector(selectLocaleObject);
	const typedTranslations = useTypedTranslations(ReadableDateStringTextKeys);

	const getRelativeDate = useCallback(
		(dateString: string) => {
			const monthOptions = options?.formatMonthOptions || { month: 'long' };
			const month = getTranslatedDate(dateString, code, monthOptions);

			const dayOptions = options?.formatDayOptions ?? { day: 'numeric' };
			const numericDay = getTranslatedDate(dateString, code, dayOptions);

			const weekdayOptions = options?.formatWeekdayOptions ?? { weekday: 'long' };
			const weekday = getTranslatedDate(dateString, code, weekdayOptions);
			const capitalizedWeekday = capitalizeFirstLetter(weekday);

			if (typedTranslations.isLoadingTranslations) {
				return { relativeDay: '', numericDay, month };
			}

			if (isDateToday(dateString)) {
				return {
					relativeDay: typedTranslations.translations.TODAY,
					numericDay,
					month
				};
			}

			if (isDateTomorrow(dateString)) {
				return {
					relativeDay: typedTranslations.translations.TOMORROW,
					numericDay,
					month
				};
			}

			return {
				relativeDay: capitalizedWeekday,
				numericDay,
				month
			};
		},
		[
			code,
			typedTranslations.isLoadingTranslations,
			typedTranslations.translations,
			options?.formatMonthOptions,
			options?.formatDayOptions,
			options?.formatWeekdayOptions
		]
	);

	return getRelativeDate;
}

export default useGetRelativeDate;
